import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import Projects from './pages/Projects';
import About from './pages/About';
import Contact from './pages/Contact';
import JoinUs from './pages/JoinUs';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminPagesList from './pages/AdminPagesList';
import AdminPageForm from './pages/AdminPageForm';
import AdminPageBuilder from './pages/AdminPageBuilder';
import Preview from './pages/Preview';

function PrivateRoute({ children }: { children: JSX.Element }) {
  const isAuth = localStorage.getItem('admin-auth') === 'true';
  return isAuth ? children : <Navigate to="/admin/login" replace />;
}

function App() {
  const [language, setLanguage] = useState<'ar' | 'en'>('ar');
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();
  const hideNavFooter = location.pathname.startsWith('/admin');

  // Load dark mode preference from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Update html class and localStorage when darkMode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const handleLanguageChange = (newLanguage: 'ar' | 'en') => {
    setLanguage(newLanguage);
    document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage;
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} transition-colors`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {!hideNavFooter && <Navbar language={language} onLanguageChange={handleLanguageChange} />}
      <main>
        <Routes>
          <Route path="/" element={<Home language={language} />} />
          <Route path="/services" element={<Services language={language} />} />
          <Route path="/projects" element={<Projects language={language} />} />
          <Route path="/about" element={<About language={language} />} />
          <Route path="/contact" element={<Contact language={language} />} />
          <Route path="/join-us" element={<JoinUs />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
          <Route path="/admin/pages" element={<PrivateRoute><AdminPagesList /></PrivateRoute>} />
          <Route path="/admin/pages/new" element={<PrivateRoute><AdminPageForm /></PrivateRoute>} />
          <Route path="/admin/pages/:id/edit" element={<PrivateRoute><AdminPageForm /></PrivateRoute>} />
          <Route path="/admin/pages/:id/builder" element={<PrivateRoute><AdminPageBuilder /></PrivateRoute>} />
          <Route path="/preview" element={<Preview />} />
        </Routes>
      </main>
      {!hideNavFooter && <Footer language={language} />}
    </div>
  );
}

export default App; 